import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Target, Briefcase, Linkedin, Instagram } from 'lucide-react';

const Home = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stats = [
    { number: '500+', label: 'Clients Served', icon: Users },
    { number: '2.5M+', label: 'Leads Generated', icon: Target },
    { number: '340%', label: 'Avg ROI Increase', icon: TrendingUp },
  ];

  return (
    <div className="pt-16">
      <section className="relative min-h-screen flex items-center" style={{
        background: 'linear-gradient(135deg, #002855 0%, #003A75 50%, #002855 100%)'
      }} data-testid="hero-section">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(197, 160, 89, 0.4) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(197, 160, 89, 0.3) 0%, transparent 50%)'
        }}></div>
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, transparent 1px, transparent 40px, rgba(255,255,255,0.03) 41px), repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0px, transparent 1px, transparent 40px, rgba(255,255,255,0.03) 41px)'
        }}></div>
        <div className="max-w-7xl mx-auto px-6 py-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeIn}>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-block text-secondary-main font-body uppercase tracking-widest text-xs mb-4"
                data-testid="hero-subtitle"
              >
                LinkedIn Marketing Experts
              </motion.span>
              <h1 className="font-heading text-5xl lg:text-7xl font-bold text-text-inverse mb-6 leading-tight tracking-tight" data-testid="hero-title">
                Elevate Your <span className="italic text-secondary-main">LinkedIn</span> Presence
              </h1>
              <p className="text-text-inverse/80 text-lg leading-relaxed mb-8" data-testid="hero-description">
                Strategic LinkedIn marketing for ambitious SMBs, MSBs and individuals worldwide. Transform your professional network into measurable business growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/enquiry"
                  data-testid="hero-cta-primary"
                  className="bg-secondary-main text-primary-dark hover:bg-secondary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-flex items-center justify-center gap-2 transition-all group"
                >
                  Start Growing Today
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-secondary"
                  className="border border-text-inverse text-text-inverse hover:bg-white/10 rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-flex items-center justify-center transition-all"
                >
                  Explore Services
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block relative"
            >
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 hover:bg-white/15 transition-all">
                  <div className="w-12 h-12 bg-secondary-main rounded-full flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-primary-dark" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-text-inverse mb-2">500+</h3>
                  <p className="text-text-inverse/80 text-sm">Happy Clients</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 mt-12 hover:bg-white/15 transition-all">
                  <div className="w-12 h-12 bg-secondary-main rounded-full flex items-center justify-center mb-4">
                    <Target className="w-6 h-6 text-primary-dark" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-text-inverse mb-2">2.5M+</h3>
                  <p className="text-text-inverse/80 text-sm">Leads Generated</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 -mt-6 hover:bg-white/15 transition-all">
                  <div className="w-12 h-12 bg-secondary-main rounded-full flex items-center justify-center mb-4">
                    <TrendingUp className="w-6 h-6 text-primary-dark" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-text-inverse mb-2">340%</h3>
                  <p className="text-text-inverse/80 text-sm">Avg ROI</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 mt-6 hover:bg-white/15 transition-all">
                  <div className="w-12 h-12 bg-secondary-main rounded-full flex items-center justify-center mb-4">
                    <Briefcase className="w-6 h-6 text-primary-dark" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-text-inverse mb-2">Global</h3>
                  <p className="text-text-inverse/80 text-sm">Worldwide Reach</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center border border-primary-main/10 p-8 hover:shadow-xl transition-shadow"
                  data-testid={`stat-card-${index}`}
                >
                  <Icon className="w-12 h-12 mx-auto mb-4 text-secondary-main" data-testid={`stat-icon-${index}`} />
                  <h3 className="font-heading text-4xl font-bold text-primary-main mb-2" data-testid={`stat-number-${index}`}>{stat.number}</h3>
                  <p className="text-text-secondary font-body" data-testid={`stat-label-${index}`}>{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-32 bg-gradient-to-br from-background-main to-white" data-testid="linkedin-importance-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-4xl lg:text-6xl font-bold text-primary-main mb-6 leading-tight"
            >
              Why LinkedIn is <span className="italic text-secondary-main">Essential</span> for Your Success
            </motion.h2>
            <p className="text-text-secondary text-xl max-w-4xl mx-auto leading-relaxed">
              In today's digital-first business world, LinkedIn isn't just a platform—it's your most powerful professional asset.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 border-l-4 border-secondary-main shadow-lg"
            >
              <h3 className="font-heading text-3xl font-bold text-primary-main mb-6">The Power of LinkedIn in Modern Business</h3>
              <div className="space-y-4 text-text-secondary leading-relaxed">
                <p>
                  <strong className="text-primary-main">LinkedIn has evolved far beyond a simple job-seeking platform.</strong> With over 900 million professionals worldwide, it's the world's largest professional network and the primary platform where business decisions are made, partnerships are formed, and careers are transformed.
                </p>
                <p>
                  Whether you're a job seeker looking for your next opportunity, an entrepreneur building your brand, or an established business seeking growth, LinkedIn provides unparalleled access to decision-makers, industry leaders, and potential clients across every sector imaginable.
                </p>
                <p>
                  <strong className="text-primary-main">Your LinkedIn profile is your digital handshake</strong>—often the first impression you make on potential employers, clients, or partners. In an era where 87% of recruiters use LinkedIn to vet candidates and 80% of B2B leads come from LinkedIn, your presence on this platform directly impacts your professional success.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-primary-dark text-text-inverse p-10 shadow-lg"
            >
              <h3 className="font-heading text-3xl font-bold mb-6">Why LinkedIn Matters for Your Life & Career</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-secondary-main rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary-dark font-bold">1</span>
                  </div>
                  <div>
                    <strong className="text-secondary-main">Career Opportunities:</strong> 77% of jobs are posted on LinkedIn. Being active means you're visible to thousands of recruiters actively searching for talent like yours.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-secondary-main rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary-dark font-bold">2</span>
                  </div>
                  <div>
                    <strong className="text-secondary-main">Professional Credibility:</strong> A complete, optimized LinkedIn profile builds trust and establishes you as a serious professional in your field.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-secondary-main rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary-dark font-bold">3</span>
                  </div>
                  <div>
                    <strong className="text-secondary-main">Business Growth:</strong> For businesses, LinkedIn is where you connect with decision-makers, generate quality B2B leads, and build strategic partnerships.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-secondary-main rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary-dark font-bold">4</span>
                  </div>
                  <div>
                    <strong className="text-secondary-main">Thought Leadership:</strong> Share insights, publish articles, and position yourself as an industry expert—building a personal brand that opens doors.
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>

          <div className="bg-white p-12 shadow-xl mb-20">
            <h3 className="font-heading text-3xl font-bold text-primary-main mb-8 text-center">How Professional Networking Actually Works on LinkedIn</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-secondary-main rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-primary-dark" />
                </div>
                <h4 className="font-body font-semibold text-primary-main mb-3">Build Connections</h4>
                <p className="text-sm text-text-secondary">Connect strategically with industry peers, potential clients, and thought leaders in your field.</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-secondary-main rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-primary-dark" />
                </div>
                <h4 className="font-body font-semibold text-primary-main mb-3">Engage Consistently</h4>
                <p className="text-sm text-text-secondary">Share valuable content, comment thoughtfully, and participate in industry discussions to stay visible.</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-secondary-main rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-primary-dark" />
                </div>
                <h4 className="font-body font-semibold text-primary-main mb-3">Provide Value</h4>
                <p className="text-sm text-text-secondary">Share insights, help others, and establish yourself as a resource—not just someone seeking opportunities.</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-secondary-main rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-8 h-8 text-primary-dark" />
                </div>
                <h4 className="font-body font-semibold text-primary-main mb-3">Convert Relationships</h4>
                <p className="text-sm text-text-secondary">Transform connections into collaborations, clients, job offers, and meaningful professional relationships.</p>
              </motion.div>
            </div>
            <div className="mt-10 p-6 bg-background-main border-l-4 border-primary-main">
              <p className="text-text-secondary">
                <strong className="text-primary-main">The truth about networking:</strong> It's not about collecting connections—it's about cultivating relationships. Effective LinkedIn networking requires strategy, consistency, and authenticity. It's about being genuinely helpful, sharing valuable insights, and building trust over time. When done right, your network becomes your net worth.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary-dark to-primary-main text-text-inverse p-12 shadow-2xl">
            <h3 className="font-heading text-4xl font-bold mb-8 text-center">Why Your Business Needs a LinkedIn Expert</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div>
                <h4 className="font-heading text-2xl font-semibold text-secondary-main mb-4">The Challenge</h4>
                <p className="text-text-inverse/90 leading-relaxed mb-4">
                  LinkedIn's algorithm is complex and constantly evolving. What worked last year may not work today. Between optimizing your profile, creating engaging content, strategic networking, lead generation, and staying ahead of platform updates, <strong>effective LinkedIn marketing requires specialized expertise and dedicated time</strong>—resources most businesses simply don't have.
                </p>
                <p className="text-text-inverse/90 leading-relaxed">
                  Most business owners and professionals spend less than 30 minutes per week on LinkedIn, yet expect results. The reality? Your competitors who are investing in professional LinkedIn management are capturing the opportunities you're missing.
                </p>
              </div>

              <div>
                <h4 className="font-heading text-2xl font-semibold text-secondary-main mb-4">The Solution</h4>
                <ul className="space-y-3 text-text-inverse/90">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-secondary-main rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-primary-dark text-xs font-bold">✓</span>
                    </div>
                    <span><strong>Expertise:</strong> LinkedIn experts stay current with algorithm changes, best practices, and proven strategies that actually generate results.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-secondary-main rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-primary-dark text-xs font-bold">✓</span>
                    </div>
                    <span><strong>Time Savings:</strong> Focus on running your business while professionals handle your LinkedIn presence strategically.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-secondary-main rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-primary-dark text-xs font-bold">✓</span>
                    </div>
                    <span><strong>Proven Systems:</strong> Access to data-driven strategies, content frameworks, and outreach templates that have generated millions in revenue.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-secondary-main rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-primary-dark text-xs font-bold">✓</span>
                    </div>
                    <span><strong>Measurable ROI:</strong> Professional LinkedIn management delivers trackable results—more profile views, quality connections, inbound leads, and business opportunities.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-secondary-main rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-primary-dark text-xs font-bold">✓</span>
                    </div>
                    <span><strong>Competitive Advantage:</strong> While others post randomly and hope for results, you'll have a strategic, optimized presence that consistently attracts opportunities.</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 border border-white/20">
              <p className="text-lg text-text-inverse leading-relaxed mb-6">
                <strong className="text-secondary-main">The bottom line:</strong> LinkedIn success isn't about luck—it's about strategy, consistency, and expertise. Hiring a LinkedIn expert is an investment in your professional growth, business development, and competitive positioning. The question isn't whether you can afford to hire an expert; it's whether you can afford not to.
              </p>
              <div className="text-center">
                <Link
                  to="/enquiry"
                  className="bg-secondary-main text-primary-dark hover:bg-secondary-light rounded-none px-10 py-4 uppercase tracking-widest text-sm font-semibold inline-flex items-center gap-2 transition-all"
                >
                  Transform Your LinkedIn Presence
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-background-main" data-testid="intro-section">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-heading text-4xl lg:text-6xl font-bold text-primary-main mb-8 leading-tight"
            data-testid="intro-heading"
          >
            LinkedIn Marketing That Delivers <span className="italic text-secondary-main">Real Results</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-text-secondary text-lg leading-relaxed mb-12"
            data-testid="intro-description"
          >
            We specialize in transforming LinkedIn profiles into powerful business development engines. Our data-driven approach combines strategic content creation, targeted outreach, and conversion optimization to help SMBs compete globally.
          </motion.p>
          <Link
            to="/services"
            data-testid="intro-cta"
            className="bg-primary-main text-white hover:bg-primary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-flex items-center gap-2 transition-all"
          >
            Discover Our Approach
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="visual-showcase">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-primary-main mb-4">
              Real Teams, Real Results
            </h2>
            <p className="text-text-secondary text-lg">
              Empowering professionals and teams worldwide to achieve LinkedIn marketing success.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative overflow-hidden group"
            >
              <img
                src="https://images.unsplash.com/photo-1758691737138-7b9b1884b1db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBlb3BsZSUyMHdvcmtpbmclMjB0b2dldGhlciUyMG9mZmljZSUyMHRlYW18ZW58MHx8fHwxNzcxNDMyOTQzfDA&ixlib=rb-4.1.0&q=85"
                alt="Team celebrating success"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <p className="text-text-inverse text-sm font-semibold">Celebrating Success Together</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative overflow-hidden group"
            >
              <img
                src="https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBlb3BsZSUyMHdvcmtpbmclMjB0b2dldGhlciUyMG9mZmljZSUyMHRlYW18ZW58MHx8fHwxNzcxNDMyOTQzfDA&ixlib=rb-4.1.0&q=85"
                alt="Conference room discussion"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <p className="text-text-inverse text-sm font-semibold">Strategic Planning</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="relative overflow-hidden group"
            >
              <img
                src="https://images.unsplash.com/photo-1760611656007-f767a8082758?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBlb3BsZSUyMHdvcmtpbmclMjB0b2dldGhlciUyMG9mZmljZSUyMHRlYW18ZW58MHx8fHwxNzcxNDMyOTQzfDA&ixlib=rb-4.1.0&q=85"
                alt="Team collaboration"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <p className="text-text-inverse text-sm font-semibold">Collaborative Workspace</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="relative overflow-hidden group"
            >
              <img
                src="https://images.unsplash.com/photo-1748346674126-8c0df10f2f61?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBlb3BsZSUyMHdvcmtpbmclMjB0b2dldGhlciUyMG9mZmljZSUyMHRlYW18ZW58MHx8fHwxNzcxNDMyOTQzfDA&ixlib=rb-4.1.0&q=85"
                alt="Team meeting"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <p className="text-text-inverse text-sm font-semibold">Professional Meetings</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="relative overflow-hidden group"
            >
              <img
                src="https://images.pexels.com/photos/5676679/pexels-photo-5676679.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Young professionals collaborating"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <p className="text-text-inverse text-sm font-semibold">Innovation & Growth</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="relative overflow-hidden group"
            >
              <img
                src="https://images.pexels.com/photos/7658280/pexels-photo-7658280.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Diverse team discussion"
                className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                <p className="text-text-inverse text-sm font-semibold">Global Team Success</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary-dark text-text-inverse" data-testid="social-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl lg:text-5xl font-bold mb-4">
              Connect With <span className="text-secondary-main">GrowthPop</span>
            </h2>
            <p className="text-text-inverse/80 text-lg max-w-3xl mx-auto">
              Follow us on LinkedIn and Instagram for daily tips, success stories, and LinkedIn marketing insights.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 text-center hover:bg-white/15 transition-all"
            >
              <div className="w-16 h-16 bg-secondary-main rounded-full flex items-center justify-center mx-auto mb-4">
                <Linkedin className="w-8 h-8 text-primary-dark" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-3">Follow on LinkedIn</h3>
              <p className="text-text-inverse/80 mb-6 text-sm">
                Connect with Jassven Salluja for exclusive LinkedIn marketing strategies and industry insights.
              </p>
              <a
                href="https://www.linkedin.com/in/jassven-salluja"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-secondary-main text-primary-dark hover:bg-secondary-light rounded-none px-6 py-3 uppercase tracking-widest text-xs font-semibold inline-block transition-all"
                data-testid="linkedin-follow-button"
              >
                Connect on LinkedIn
              </a>
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-xs text-text-inverse/60">@jassven-salluja</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 text-center hover:bg-white/15 transition-all"
            >
              <div className="w-16 h-16 bg-secondary-main rounded-full flex items-center justify-center mx-auto mb-4">
                <Instagram className="w-8 h-8 text-primary-dark" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-3">Follow on Instagram</h3>
              <p className="text-text-inverse/80 mb-6 text-sm">
                Get daily motivation, quick tips, and behind-the-scenes content from our LinkedIn marketing agency.
              </p>
              <a
                href="https://www.instagram.com/jassvensalluja"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-secondary-main text-primary-dark hover:bg-secondary-light rounded-none px-6 py-3 uppercase tracking-widest text-xs font-semibold inline-block transition-all"
                data-testid="instagram-follow-button"
              >
                Follow on Instagram
              </a>
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-xs text-text-inverse/60">@jassvensalluja</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;